import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import { errorMessage } from '../lib/properties.js';
import { getTenantSettings, setCommissionGoal } from '../lib/assistant-settings-api.js';
import { OVERVIEW_QUERY_KEY } from '../lib/overview-api.js';

/**
 * Krok GRAF2 — MESICNI CIL PROVIZE kanceláře (Nastavení → Ekonomika).
 *
 * Zadání: „cíl nastavitelný v Nastavení kanceláře; bez cíle karta bez čáry."
 * Prázdné pole proto NENÍ chyba — je to pokyn „cíl zrušit“ a karta Přehledu
 * se pak kreslí bez čáry. Nula se nepřijímá (splněný cíl hned po uložení).
 *
 * Měna není volba: je to měna kanceláře, kterou nese už uložený cíl, jinak
 * CZK (jurisdikce CZ). Druhý přepínač měny by se rozešel s doklady.
 */
const TENANT_SETTINGS_QUERY_KEY = ['tenant-settings'] as const;
const DEFAULT_CURRENCY = 'CZK';

export function CommissionGoalCard(): React.JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: TENANT_SETTINGS_QUERY_KEY, queryFn: getTenantSettings });
  const [draft, setDraft] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const saved = settings.data?.commissionGoal ?? null;
  // Rozepsaná hodnota má přednost před serverovou, dokud se neuloží.
  const value = draft ?? saved?.amount ?? '';

  const save = (): void => {
    const trimmed = value.trim();
    const goal =
      trimmed === ''
        ? null
        : { amount: trimmed, currency: saved?.currency ?? DEFAULT_CURRENCY };
    setBusy(true);
    setError(null);
    void setCommissionGoal(goal)
      .then((next) => {
        queryClient.setQueryData(TENANT_SETTINGS_QUERY_KEY, next);
        setDraft(null);
        // Karta Přehledu čte cíl ze souhrnu — bez tohohle by držela starý.
        void queryClient.invalidateQueries({ queryKey: OVERVIEW_QUERY_KEY });
      })
      .catch((e: unknown) => {
        setError(t('webAdmin.prehled.commission.saveError', { error: errorMessage(e) }));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div
      className="rh-card"
      style={{ maxWidth: 640, padding: 16, marginBottom: 16 }}
      data-testid="nastaveni-commission-goal-card"
    >
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.prehled.commission.settingsTitle')}</h2>
      <p className="mb-2 text-sm text-gray-600">{t('webAdmin.prehled.commission.settingsHint')}</p>
      <label className="rh-a2-field">
        <span className="rh-a2-label">
          {t('webAdmin.prehled.commission.settingsLabel', {
            currency: saved?.currency ?? DEFAULT_CURRENCY,
          })}
        </span>
        <input
          className="rh-a2-input"
          type="text"
          inputMode="decimal"
          value={value}
          disabled={busy || settings.data === undefined}
          data-testid="nastaveni-commission-goal-input"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
      </label>
      <button
        type="button"
        className="rh-v4-btn rh-v4-btn--tinted"
        disabled={busy || settings.data === undefined}
        data-testid="nastaveni-commission-goal-save"
        onClick={save}
      >
        {t('webAdmin.prehled.commission.settingsSave')}
      </button>
      {error !== null ? (
        <p role="alert" className="rh-a2-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
